'use client';
import React, { useState } from 'react';
import { FormField } from '@common/bw-ui/ui/form-field';
import { PasswordInput } from '@common/bw-ui/ui/password-input';
import { PasswordCriteria } from '@common/bw-ui/ui/password-criteria';
import { passwordRules, validatePassword } from '@/utils/helper/PasswordValidation';

export interface IPasswordFieldsProps {
  password: string;
  confirmPassword: string;
  setPassword: (password: string) => void;
  setConfirmPassword: (confirmPassword: string) => void;
}

function passwordCriteria(password: string) {
  return passwordRules.map(rule => ({ label: rule.label, met: rule.test(password) }));
}

/**
 * The password + confirm password pair used during signup. The values live in the parent's local
 * state (never in the signup details/session) and travel to the server via the extras
 * side-channel.
 */
export function PasswordFields(props: IPasswordFieldsProps) {
  const [showPasswordError, setShowPasswordError] = useState<boolean>(false);

  const { password, confirmPassword } = props;

  const validPassword = password.length === 0 || validatePassword(password);
  const passwordsMatch = password === confirmPassword;

  return (
    <>
      <FormField
        id="password"
        label="Password"
        requirement="required"
        error={
          !validPassword && showPasswordError ? (
            <span data-testid="passwordValidationMessage">Password does not meet criteria.</span>
          ) : undefined
        }>
        <PasswordInput
          name="password"
          placeholder="Enter your password"
          value={password}
          onChange={e => props.setPassword(e.target.value)}
          onBlur={() => setShowPasswordError(true)}
          success={password.length > 0 && validatePassword(password)}
          data-testid="password"
          data-private
        />
      </FormField>
      <FormField
        id="confirmPassword"
        label="Confirm Password"
        requirement="required"
        error={
          !passwordsMatch && confirmPassword.length > 0 ? (
            <span data-testid="passwordsMatchError">Passwords do not match.</span>
          ) : undefined
        }>
        <PasswordInput
          name="confirmPassword"
          placeholder="Confirm your password"
          value={confirmPassword}
          onChange={e => props.setConfirmPassword(e.target.value)}
          success={confirmPassword.length > 0 && passwordsMatch}
          data-testid="confirmPassword"
          data-private
        />
      </FormField>

      <PasswordCriteria criteria={passwordCriteria(password)} />
    </>
  );
}
